import { Link } from "react-router-dom";
import useSeo from "@/lib/useSeo";

const CONTACT_EMAIL = "Gallenhunt90@gmail.com";

export default function Contact() {
  useSeo({
    title: "Contact · The Couponer",
    description:
      "Get in touch with The Couponer — deal tips, corrections, sponsorship, or press inquiries.",
    canonical: "https://thecouponer.com/contact",
  });

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-10 py-12" data-testid="contact-page">
      <div className="eyebrow text-[10px] text-[#5c5c5c] mb-2">GET IN TOUCH</div>
      <h1 className="serif text-5xl md:text-6xl leading-tight tracking-tight mb-6">
        Say hello.
      </h1>
      <p className="text-[15px] leading-relaxed text-[#3a3a3a] mb-10 max-w-2xl">
        A real person reads every email. Whether you spotted a broken code, want to sponsor a
        placement, or just want to chat about couponing — the inbox is open.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
        <div className="border-2 border-black bg-[#fff8ef] p-6" data-testid="contact-card-general">
          <div className="eyebrow text-[10px] text-[#5c5c5c] mb-2">GENERAL</div>
          <h2 className="serif text-2xl mb-2">Support &amp; feedback</h2>
          <p className="text-sm text-[#3a3a3a] mb-4 leading-relaxed">
            Broken codes, wrong prices, account issues, or feature requests. Response within 24-48
            hours.
          </p>
          <a
            href={`mailto:${CONTACT_EMAIL}?subject=The%20Couponer%20—%20Support`}
            className="inline-block bg-black text-white px-4 py-2 text-xs tracking-[0.2em] mono hover:bg-[#ff6b6b] transition-colors"
            data-testid="contact-support-mail"
          >
            EMAIL SUPPORT →
          </a>
        </div>

        <div className="border-2 border-black bg-[#4ecdc4]/20 p-6" data-testid="contact-card-sponsor">
          <div className="eyebrow text-[10px] text-[#5c5c5c] mb-2">RETAILERS</div>
          <h2 className="serif text-2xl mb-2">Sponsor a deal</h2>
          <p className="text-sm text-[#3a3a3a] mb-4 leading-relaxed">
            Featured slots, Deal of the Day, retailer takeovers. Self-serve pricing on our sponsor
            page.
          </p>
          <Link
            to="/sponsor"
            className="inline-block bg-black text-white px-4 py-2 text-xs tracking-[0.2em] mono hover:bg-[#ff6b6b] transition-colors"
            data-testid="contact-sponsor-link"
          >
            SEE SPONSOR TIERS →
          </Link>
        </div>

        <div className="border-2 border-black bg-white p-6" data-testid="contact-card-press">
          <div className="eyebrow text-[10px] text-[#5c5c5c] mb-2">PRESS</div>
          <h2 className="serif text-2xl mb-2">Media &amp; press</h2>
          <p className="text-sm text-[#3a3a3a] mb-4 leading-relaxed">
            Interviews, quotes for a story, retail-trend data. Same-day reply for verified press.
          </p>
          <a
            href={`mailto:${CONTACT_EMAIL}?subject=Press%20Inquiry%20—%20The%20Couponer`}
            className="inline-block bg-black text-white px-4 py-2 text-xs tracking-[0.2em] mono hover:bg-[#ff6b6b] transition-colors"
            data-testid="contact-press-mail"
          >
            PRESS INQUIRIES →
          </a>
        </div>

        <div className="border-2 border-black bg-[#ff6b6b]/10 p-6" data-testid="contact-card-legal">
          <div className="eyebrow text-[10px] text-[#5c5c5c] mb-2">LEGAL</div>
          <h2 className="serif text-2xl mb-2">Privacy &amp; DMCA</h2>
          <p className="text-sm text-[#3a3a3a] mb-4 leading-relaxed">
            Data access requests, deletion, GDPR/CCPA, or copyright concerns.
          </p>
          <a
            href={`mailto:${CONTACT_EMAIL}?subject=Legal%20—%20The%20Couponer`}
            className="inline-block bg-black text-white px-4 py-2 text-xs tracking-[0.2em] mono hover:bg-[#ff6b6b] transition-colors"
            data-testid="contact-legal-mail"
          >
            EMAIL LEGAL →
          </a>
        </div>
      </div>

      <div className="border-t-2 border-black pt-8">
        <h2 className="serif text-2xl mb-4">The essentials</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-[15px] leading-relaxed">
          <div>
            <div className="eyebrow text-[10px] text-[#5c5c5c] mb-1">EMAIL</div>
            <p>
              <a href={`mailto:${CONTACT_EMAIL}`} className="underline" data-testid="contact-email-link">
                {CONTACT_EMAIL}
              </a>
            </p>
          </div>
          <div>
            <div className="eyebrow text-[10px] text-[#5c5c5c] mb-1">HOURS</div>
            <p>Mon-Fri, 9am-6pm ET · Weekend replies best-effort</p>
          </div>
          <div>
            <div className="eyebrow text-[10px] text-[#5c5c5c] mb-1">BASED IN</div>
            <p>United States</p>
          </div>
          <div>
            <div className="eyebrow text-[10px] text-[#5c5c5c] mb-1">FOUNDED</div>
            <p>2026 · Independent &amp; bootstrapped</p>
          </div>
        </div>
      </div>

      <p className="text-sm text-[#5c5c5c] mt-12">
        Learn more:{" "}
        <Link to="/about" className="underline">
          About us
        </Link>{" "}
        ·{" "}
        <Link to="/privacy" className="underline">
          Privacy Policy
        </Link>{" "}
        ·{" "}
        <Link to="/terms" className="underline">
          Terms of Service
        </Link>
      </p>
    </div>
  );
}
